import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

const EMPTY = { income: [], expense: [], transfer: [], investment: [], items: [] };
const CategoriesContext = createContext(null);

/** Per-user category lists (all DB-backed, four type groups), shared by every dropdown. */
export function CategoriesProvider({ children }) {
  const [lists, setLists] = useState(EMPTY);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setLists(data);
    } catch (e) {
      logError("Categories load failed", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addCategory = useCallback(async (name, kind) => {
    const { data } = await api.post("/categories", { name, kind });
    const { category, ...rest } = data;
    setLists(rest);
    return category.name;
  }, []);

  const updateCategory = useCallback(async (id, name, kind) => {
    const { data } = await api.put(`/categories/${id}`, { name, kind });
    setLists(data);
  }, []);

  const deleteCategory = useCallback(async (id) => {
    const { data } = await api.delete(`/categories/${id}`);
    setLists(data);
  }, []);

  const value = useMemo(
    () => ({ ...lists, addCategory, updateCategory, deleteCategory }),
    [lists, addCategory, updateCategory, deleteCategory]
  );

  return <CategoriesContext.Provider value={value}>{children}</CategoriesContext.Provider>;
}

/** Safe fallback outside the provider (public pages) — empty lists, no actions. */
export function useCategories() {
  return useContext(CategoriesContext) ?? {
    ...EMPTY, addCategory: null, updateCategory: null, deleteCategory: null,
  };
}
